import {
  ReactElement, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import { Helmet } from 'react-helmet';
import {
  Redirect, useHistory, useLocation, useParams,
} from 'react-router-dom';

import { Loader } from 'components';
import { getUserProfile } from 'helpers/userProfile';
import { apiCallHtml } from '~/providers';
import { AdminLayout } from '../AdminLayout';
import { DebugToolbar } from './components/DebugToolbar';
import { buildDebugDocument, isDebugPageMessage } from './helpers/buildDebugDocument';
import { buildDebugRoute, resolveDebugHref } from './helpers/resolveDebugHref';
import {
  Container,
  ContentFrame,
  ErrorBox,
  LoaderWrapper,
} from './styles';

const AUTO_REFRESH_INTERVAL_MS = 10000;
const INITIAL_FRAME_HEIGHT = 320;

export const BackendDebugPage = (): ReactElement => {
  const history = useHistory();
  const location = useLocation();
  const { pagePath } = useParams<{ pagePath?: string }>();
  const frameRef = useRef<HTMLIFrameElement>(null);

  const [profile] = useState(getUserProfile);
  const [html, setHtml] = useState<string|null>(null);
  const [errorMessage, setErrorMessage] = useState<string|null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAutoRefreshOn, setIsAutoRefreshOn] = useState(false);
  const [frameHeight, setFrameHeight] = useState(INITIAL_FRAME_HEIGHT);
  const [refreshToken, setRefreshToken] = useState(0);

  const currentPagePath = pagePath ?? '';
  const isAllowed = !!profile.permissions.isAdminSistema || !!profile.isMasterUser;
  const frameDocument = useMemo(() => (html === null ? null : buildDebugDocument(html)), [html]);

  const handleRefresh = useCallback(() => {
    setRefreshToken((previous) => previous + 1);
  }, []);

  const handleNavigate = useCallback((href: string, newTab: boolean) => {
    const target = resolveDebugHref(currentPagePath, href);
    if (target.kind === 'ignore') return;

    if (target.kind === 'external') {
      window.open(target.url, '_blank', 'noopener,noreferrer');
      return;
    }

    const route = buildDebugRoute(target.pagePath, target.query);
    if (newTab) {
      window.open(route, '_blank', 'noopener,noreferrer');
      return;
    }
    history.push(route);
  }, [currentPagePath, history]);

  useEffect(() => {
    if (!isAllowed) return undefined;

    let isCancelled = false;
    setIsLoading(true);
    setErrorMessage(null);

    apiCallHtml({ pagePath: currentPagePath, query: location.search })
      .then((response) => {
        if (isCancelled) return;
        setHtml(response);
      })
      .catch((err) => {
        if (isCancelled) return;
        console.error(err);
        const status = err?.response?.status;
        setHtml(null);
        setErrorMessage(status ? `Não foi possível carregar a página de debug. (HTTP ${status})` : 'Não foi possível carregar a página de debug.');
      })
      .finally(() => {
        if (isCancelled) return;
        setIsLoading(false);
      });

    return () => { isCancelled = true; };
  }, [isAllowed, currentPagePath, location.search, refreshToken]);

  useEffect(() => {
    if (!isAutoRefreshOn) return undefined;
    const intervalId = setInterval(handleRefresh, AUTO_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [isAutoRefreshOn, handleRefresh]);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      // O iframe roda numa origem opaca (`event.origin === 'null'`), então a identidade da janela
      // é a única checagem confiável de que a mensagem veio da página de debug.
      if (!frameRef.current || event.source !== frameRef.current.contentWindow) return;
      if (!isDebugPageMessage(event.data)) return;

      if (event.data.type === 'height') {
        setFrameHeight(event.data.height);
        return;
      }
      handleNavigate(event.data.href, event.data.newTab);
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleNavigate]);

  if (!isAllowed) return <Redirect to="/visao-geral" />;

  return (
    <>
      <Helmet>
        <title>Diel Energia - Páginas de Debug</title>
      </Helmet>
      <AdminLayout />
      <Container>
        <DebugToolbar
          pagePath={currentPagePath}
          state={{ isLoading, isAutoRefreshOn }}
          handlers={{ onRefresh: handleRefresh, onToggleAutoRefresh: setIsAutoRefreshOn }}
        />
        {errorMessage ? <ErrorBox>{errorMessage}</ErrorBox> : null}
        {(isLoading && frameDocument === null) ? (
          <LoaderWrapper><Loader variant="primary" /></LoaderWrapper>
        ) : null}
        {frameDocument !== null ? (
          <ContentFrame
            ref={frameRef}
            title="Páginas de Debug"
            srcDoc={frameDocument}
            height={frameHeight}
            // Sem `allow-same-origin`: o HTML do backend não alcança o token nem o DOM do app.
            sandbox="allow-scripts allow-forms"
          />
        ) : null}
      </Container>
    </>
  );
};
